import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { Search, Droplet, Zap, Thermometer, Wrench, Sparkles, CheckCircle, Users, Clock, Shield, ArrowRight, Star, Car, MapPin, DollarSign, BadgeCheck, FileText, HandCoins, MessageCircle, Award, Quote, TrendingUp, Building2, Home, Bug, Hammer, Trees, Truck, DoorOpen, Settings } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// Service category to translation key mapping
const categoryTranslationKeys = {
  plumbing: "plumbing",
  electrical: "electrical",
  hvac: "hvac",
  handyman: "handyman",
  cleaning: "cleaning",
  mechanics: "mechanics",
  roofing: "roofing",
  "pest-control": "pestControl",
  remodeling: "remodeling",
  landscaping: "landscaping",
  moving: "moving",
  "garage-door": "garageDoor",
  "appliance-repair": "applianceRepair",
};

// Helper to get translated category name
const getCategoryName = (category, t) => {
  const translationKey = categoryTranslationKeys[category.slug];
  if (translationKey) {
    return t(`categories.${translationKey}`);
  }
  return category.name;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const iconMap = {
  droplet: Droplet,
  zap: Zap,
  thermometer: Thermometer,
  wrench: Wrench,
  sparkles: Sparkles,
  car: Car,
  home: Home,
  bug: Bug,
  hammer: Hammer,
  trees: Trees,
  truck: Truck,
  "door-open": DoorOpen,
  settings: Settings,
};

const serviceColors = {
  plumbing: "bg-blue-100 text-blue-700",
  electrical: "bg-yellow-100 text-yellow-700",
  hvac: "bg-cyan-100 text-cyan-700",
  handyman: "bg-orange-100 text-orange-700",
  cleaning: "bg-purple-100 text-purple-700",
  mechanics: "bg-red-100 text-red-700",
  roofing: "bg-slate-100 text-slate-700",
  "pest-control": "bg-green-100 text-green-700",
  remodeling: "bg-amber-100 text-amber-700",
  landscaping: "bg-emerald-100 text-emerald-700",
  moving: "bg-indigo-100 text-indigo-700",
  "garage-door": "bg-gray-100 text-gray-700",
  "appliance-repair": "bg-pink-100 text-pink-700",
};

const HomePage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({ total_workers: 0, total_requests: 0, matched_leads: 0 });
  const [featuredJobs, setFeaturedJobs] = useState([]);
  const [topWorkers, setTopWorkers] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [successStories, setSuccessStories] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [catRes, statsRes, jobsRes, homeStatsRes, testimonialsRes, successRes] = await Promise.all([
          axios.get(`${API}/categories`),
          axios.get(`${API}/stats`),
          axios.get(`${API}/featured-jobs?limit=6`),
          axios.get(`${API}/homepage-stats`),
          axios.get(`${API}/testimonials?limit=6`),
          axios.get(`${API}/success-stories?limit=3`)
        ]);
        setCategories(catRes.data);
        setStats(statsRes.data);
        setFeaturedJobs(jobsRes.data);
        setTopWorkers(homeStatsRes.data.top_workers || []);
        setTestimonials(testimonialsRes.data || []);
        setSuccessStories(successRes.data || []);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/browse?search=${encodeURIComponent(searchQuery)}`);
    }
  };

  const handleCategoryClick = (slug) => {
    navigate(`/browse?service=${slug}`);
  };

  return (
    <div className="min-h-screen bg-white" data-testid="home-page">
      <Navbar />
      
      {/* Hero Section */}
      <section className="py-20 md:py-32 px-4" data-testid="hero-section">
        <div className="container-custom text-center">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-medium text-[#202124] tracking-tight mb-6 animate-fade-in-up">
            {t("hero.title")}
          </h1>
          <p className="text-lg md:text-xl text-[#5f6368] max-w-2xl mx-auto mb-10 animate-fade-in-up stagger-1">
            {t("hero.subtitle")}
          </p>
          
          {/* Search Bar */}
          <form onSubmit={handleSearch} className="max-w-2xl mx-auto mb-8 animate-fade-in-up stagger-2">
            <div className="relative">
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-[#80868b] w-5 h-5" />
              <input
                type="text"
                placeholder={t("hero.searchPlaceholder")}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="search-hero pl-14 pr-36"
                data-testid="hero-search-input"
              />
              <Button
                type="submit"
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-6 h-10"
                data-testid="hero-search-btn"
              >
                {t("common.search")}
              </Button>
            </div>
          </form>

          {/* Quick Category Pills */}
          <div className="flex flex-wrap justify-center gap-3 animate-fade-in-up stagger-3">
            {categories.slice(0, 5).map((cat) => {
              const Icon = iconMap[cat.icon];
              return (
                <button
                  key={cat.id}
                  onClick={() => handleCategoryClick(cat.slug)}
                  className="flex items-center gap-2 px-4 py-2 rounded-full border border-[#dadce0] text-[#5f6368] hover:bg-[#f8f9fa] hover:border-[#1a73e8]/30 transition-all text-sm"
                  data-testid={`quick-category-${cat.slug}`}
                >
                  {Icon && <Icon className="w-4 h-4" />}
                  {getCategoryName(cat, t)}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Categories Section */}
      <section className="py-16 bg-[#f8f9fa]" data-testid="categories-section">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-medium text-[#202124] text-center mb-4">
            {t("categories.title")}
          </h2>
          <p className="text-[#5f6368] text-center mb-12 max-w-xl mx-auto">
            {t("categories.subtitle")}
          </p>
          
          <div className="grid-categories">
            {loading ? (
              Array(5).fill(0).map((_, i) => (
                <div key={i} className="service-card animate-pulse">
                  <div className="w-full h-32 bg-gray-200 rounded-xl" />
                  <div className="h-6 bg-gray-200 rounded w-24" />
                </div>
              ))
            ) : (
              categories.map((category) => {
                const Icon = iconMap[category.icon];
                return (
                  <button
                    key={category.id}
                    onClick={() => handleCategoryClick(category.slug)}
                    className="service-card hover-lift"
                    data-testid={`category-card-${category.slug}`}
                  >
                    <div className="w-full h-32 rounded-xl overflow-hidden mb-2">
                      <img
                        src={category.image}
                        alt={getCategoryName(category, t)}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      {Icon && <Icon className="w-5 h-5 text-[#1a73e8]" />}
                      <span className="font-medium text-[#202124]">{getCategoryName(category, t)}</span>
                    </div>
                    <p className="text-sm text-[#5f6368]">{category.description}</p>
                  </button>
                );
              })
            )}
          </div>
        </div>
      </section>

      {/* How It Works - Enhanced with Tabs */}
      <section className="section-spacing" data-testid="how-it-works-section">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-medium text-[#202124] text-center mb-4">
            {t("howItWorks.title")}
          </h2>
          <p className="text-[#5f6368] text-center mb-12 max-w-xl mx-auto">
            {t("howItWorks.subtitle")}
          </p>

          <Tabs defaultValue="homeowners" className="max-w-5xl mx-auto">
            <TabsList className="grid w-full max-w-md mx-auto grid-cols-2 mb-12 bg-[#f8f9fa] rounded-full p-1 h-auto">
              <TabsTrigger 
                value="homeowners" 
                className="rounded-full py-3 px-6 data-[state=active]:bg-[#1a73e8] data-[state=active]:text-white transition-all"
                data-testid="tab-homeowners"
              >
                <Home className="w-4 h-4 mr-2" />
                {t("howItWorks.forHomeowners")}
              </TabsTrigger>
              <TabsTrigger 
                value="contractors" 
                className="rounded-full py-3 px-6 data-[state=active]:bg-[#1a73e8] data-[state=active]:text-white transition-all"
                data-testid="tab-contractors"
              >
                <Building2 className="w-4 h-4 mr-2" />
                {t("howItWorks.forContractors")}
              </TabsTrigger>
            </TabsList>

            <TabsContent value="homeowners" className="mt-0">
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  {
                    step: "1",
                    icon: FileText,
                    titleKey: "howItWorks.homeowner.step1Title",
                    descKey: "howItWorks.homeowner.step1Desc"
                  },
                  {
                    step: "2",
                    icon: Users,
                    titleKey: "howItWorks.homeowner.step2Title",
                    descKey: "howItWorks.homeowner.step2Desc"
                  },
                  {
                    step: "3",
                    icon: MessageCircle,
                    titleKey: "howItWorks.homeowner.step3Title",
                    descKey: "howItWorks.homeowner.step3Desc"
                  },
                  {
                    step: "4",
                    icon: CheckCircle,
                    titleKey: "howItWorks.homeowner.step4Title",
                    descKey: "howItWorks.homeowner.step4Desc"
                  }
                ].map((item, idx) => (
                  <div key={idx} className="relative" data-testid={`homeowner-step-${item.step}`}>
                    <div className="feature-card text-center h-full">
                      <div className="w-14 h-14 rounded-2xl bg-[#e8f0fe] flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-7 h-7 text-[#1a73e8]" />
                      </div>
                      <div className="text-sm font-semibold text-[#1a73e8] mb-2">Step {item.step}</div>
                      <h3 className="text-lg font-medium text-[#202124] mb-2">{t(item.titleKey)}</h3>
                      <p className="text-[#5f6368] text-sm">{t(item.descKey)}</p>
                    </div>
                    {idx < 3 && (
                      <div className="hidden md:block absolute top-1/3 -right-3 text-[#dadce0]">
                        <ArrowRight className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Button
                  onClick={() => navigate("/request")}
                  className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto text-base font-medium"
                  data-testid="get-started-homeowner-btn"
                >
                  {t("howItWorks.getStartedFree")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>

            <TabsContent value="contractors" className="mt-0">
              <div className="grid md:grid-cols-4 gap-6">
                {[
                  {
                    step: "1",
                    icon: FileText,
                    titleKey: "howItWorks.contractor.step1Title",
                    descKey: "howItWorks.contractor.step1Desc"
                  },
                  {
                    step: "2",
                    icon: Search,
                    titleKey: "howItWorks.contractor.step2Title",
                    descKey: "howItWorks.contractor.step2Desc"
                  },
                  {
                    step: "3",
                    icon: HandCoins,
                    titleKey: "howItWorks.contractor.step3Title",
                    descKey: "howItWorks.contractor.step3Desc"
                  },
                  {
                    step: "4",
                    icon: TrendingUp,
                    titleKey: "howItWorks.contractor.step4Title",
                    descKey: "howItWorks.contractor.step4Desc"
                  }
                ].map((item, idx) => (
                  <div key={idx} className="relative" data-testid={`contractor-step-${item.step}`}>
                    <div className="feature-card text-center h-full">
                      <div className="w-14 h-14 rounded-2xl bg-[#e6f4ea] flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-7 h-7 text-[#1e8e3e]" />
                      </div>
                      <div className="text-sm font-semibold text-[#1e8e3e] mb-2">Step {item.step}</div>
                      <h3 className="text-lg font-medium text-[#202124] mb-2">{t(item.titleKey)}</h3>
                      <p className="text-[#5f6368] text-sm">{t(item.descKey)}</p>
                    </div>
                    {idx < 3 && (
                      <div className="hidden md:block absolute top-1/3 -right-3 text-[#dadce0]">
                        <ArrowRight className="w-6 h-6" />
                      </div>
                    )}
                  </div>
                ))}
              </div>
              <div className="text-center mt-10">
                <Button
                  onClick={() => navigate("/join")}
                  className="bg-[#1e8e3e] hover:bg-[#137333] text-white rounded-full px-8 py-3 h-auto text-base font-medium"
                  data-testid="join-as-pro-hiw-btn"
                >
                  {t("howItWorks.joinAsProFree")} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-[#1a73e8]" data-testid="stats-section">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center text-white">
            {[
              { value: stats.total_workers || "500+", labelKey: "stats.totalPros" },
              { value: stats.total_requests || "1,000+", labelKey: "stats.jobsCompleted" },
              { value: stats.matched_leads || "95%", label: "Match Rate" },
              { value: "4.8", labelKey: "stats.avgRating", icon: Star }
            ].map((stat, idx) => (
              <div key={idx} data-testid={`stat-${idx}`}>
                <div className="text-3xl md:text-4xl font-medium mb-1 flex items-center justify-center gap-1">
                  {stat.value}
                  {stat.icon && <Star className="w-6 h-6 fill-white" />}
                </div>
                <div className="text-white/80 text-sm">{stat.labelKey ? t(stat.labelKey) : stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="section-spacing" data-testid="trust-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#202124] mb-6">
                {t("trust.title")}
              </h2>
              <div className="space-y-6">
                {[
                  { icon: Shield, titleKey: "trust.verifiedPros", descKey: "trust.verifiedDesc" },
                  { icon: Clock, titleKey: "trust.fastResponse", descKey: "trust.fastDesc" },
                  { icon: CheckCircle, titleKey: "trust.qualityGuarantee", descKey: "trust.qualityDesc" }
                ].map((item, idx) => (
                  <div key={idx} className="flex gap-4" data-testid={`trust-item-${idx}`}>
                    <div className="w-10 h-10 rounded-full bg-[#e6f4ea] flex items-center justify-center shrink-0">
                      <item.icon className="w-5 h-5 text-[#1e8e3e]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#202124] mb-1">{t(item.titleKey)}</h3>
                      <p className="text-[#5f6368] text-sm">{t(item.descKey)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            <div className="relative">
              <img
                src="https://images.unsplash.com/photo-1762500824959-ad78b31b4161?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDB8MHwxfHNlYXJjaHwyfHxoYXBweSUyMGZhbWlseSUyMGluJTIwY2xlYW4lMjBsaXZpbmclMjByb29tfGVufDB8fHx8MTc3MzE4NDc4NXww&ixlib=rb-4.1.0&q=85"
                alt="Happy family in clean home"
                className="rounded-3xl shadow-xl w-full"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Customer Testimonials Section */}
      <section className="py-16 bg-[#f8f9fa]" data-testid="testimonials-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium text-[#202124] mb-4">
              {t("testimonials.title")}
            </h2>
            <p className="text-[#5f6368] max-w-xl mx-auto">
              {t("testimonials.subtitle")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {testimonials.length > 0 ? testimonials.map((testimonial, idx) => (
              <div 
                key={testimonial.id || idx} 
                className="bg-white rounded-2xl p-6 shadow-sm border border-[#dadce0] hover:shadow-md transition-shadow"
                data-testid={`testimonial-${idx}`}
              >
                <div className="flex items-center gap-1 mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star 
                      key={i} 
                      className={`w-5 h-5 ${i < (testimonial.rating || 5) ? 'text-[#f9ab00] fill-[#f9ab00]' : 'text-gray-200'}`} 
                    />
                  ))}
                </div>
                <div className="relative mb-4">
                  <Quote className="absolute -top-2 -left-1 w-8 h-8 text-[#e8f0fe]" />
                  <p className="text-[#5f6368] leading-relaxed pl-6">
                    "{testimonial.comment || testimonial.text}"
                  </p>
                </div>
                <div className="flex items-center gap-3 pt-4 border-t border-[#f1f3f4]">
                  <div className="w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                    <span className="text-sm font-medium text-[#1a73e8]">
                      {(testimonial.customer_name || testimonial.name || "Customer")?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-[#202124] text-sm">
                      {testimonial.customer_name || testimonial.name || "Happy Customer"}
                    </p>
                    <p className="text-xs text-[#5f6368]">
                      {testimonial.service_type ? `${testimonial.service_type?.charAt(0).toUpperCase() + testimonial.service_type?.slice(1)} Service` : testimonial.location || "Verified Customer"}
                    </p>
                  </div>
                </div>
              </div>
            )) : (
              // Fallback testimonials when none from API
              [
                {
                  name: "Sarah M.",
                  rating: 5,
                  text: "Found an amazing plumber within an hour! He fixed our emergency leak and was super professional. Will definitely use LocalPros again.",
                  service: "Plumbing",
                  location: "Austin, TX"
                },
                {
                  name: "Michael R.",
                  rating: 5,
                  text: "The electrician LocalPros connected us with was fantastic. Fair pricing, showed up on time, and did excellent work on our home rewiring project.",
                  service: "Electrical",
                  location: "Denver, CO"
                },
                {
                  name: "Jennifer L.",
                  rating: 5,
                  text: "I was skeptical at first, but LocalPros made finding a reliable handyman so easy. The whole process was seamless from start to finish.",
                  service: "Handyman",
                  location: "Seattle, WA"
                },
                {
                  name: "David K.",
                  rating: 5,
                  text: "Our HVAC system broke down during a heatwave. LocalPros got us connected with a technician same day. Lifesaver!",
                  service: "HVAC",
                  location: "Phoenix, AZ"
                },
                {
                  name: "Amanda T.",
                  rating: 5,
                  text: "The cleaning service I found through LocalPros transformed our home. Professional, thorough, and reasonably priced. Highly recommend!",
                  service: "Cleaning",
                  location: "Miami, FL"
                },
                {
                  name: "Robert H.",
                  rating: 4,
                  text: "Great platform for finding local mechanics. Got my car fixed quickly and the price was fair. The reviews really helped me choose the right shop.",
                  service: "Mechanics",
                  location: "Chicago, IL"
                }
              ].map((testimonial, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-2xl p-6 shadow-sm border border-[#dadce0] hover:shadow-md transition-shadow"
                  data-testid={`testimonial-${idx}`}
                >
                  <div className="flex items-center gap-1 mb-4">
                    {[...Array(5)].map((_, i) => (
                      <Star 
                        key={i} 
                        className={`w-5 h-5 ${i < testimonial.rating ? 'text-[#f9ab00] fill-[#f9ab00]' : 'text-gray-200'}`} 
                      />
                    ))}
                  </div>
                  <div className="relative mb-4">
                    <Quote className="absolute -top-2 -left-1 w-8 h-8 text-[#e8f0fe]" />
                    <p className="text-[#5f6368] leading-relaxed pl-6">
                      "{testimonial.text}"
                    </p>
                  </div>
                  <div className="flex items-center gap-3 pt-4 border-t border-[#f1f3f4]">
                    <div className="w-10 h-10 rounded-full bg-[#e8f0fe] flex items-center justify-center">
                      <span className="text-sm font-medium text-[#1a73e8]">
                        {testimonial.name.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium text-[#202124] text-sm">{testimonial.name}</p>
                      <p className="text-xs text-[#5f6368]">{testimonial.service} Service • {testimonial.location}</p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </section>

      {/* Contractor Success Stories Section */}
      <section className="section-spacing" data-testid="success-stories-section">
        <div className="container-custom">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-[#e6f4ea] rounded-full mb-4">
              <Award className="w-4 h-4 text-[#1e8e3e]" />
              <span className="text-sm font-medium text-[#1e8e3e]">{t("successStories.title")}</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-medium text-[#202124] mb-4">
              {t("successStories.title")}
            </h2>
            <p className="text-[#5f6368] max-w-xl mx-auto">
              {t("successStories.subtitle")}
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {successStories.length > 0 ? successStories.map((story, idx) => (
              <div 
                key={story.id || idx} 
                className="bg-white rounded-2xl overflow-hidden border border-[#dadce0] hover:shadow-lg transition-all hover:-translate-y-1"
                data-testid={`success-story-${idx}`}
              >
                <div className="h-48 bg-gradient-to-br from-[#e8f0fe] to-[#f8f9fa] flex items-center justify-center">
                  {story.photo ? (
                    <img src={story.photo} alt={story.name} className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                      <span className="text-3xl font-medium text-[#1a73e8]">
                        {story.name?.charAt(0).toUpperCase()}
                      </span>
                    </div>
                  )}
                </div>
                <div className="p-6">
                  <div className="flex items-center gap-2 mb-2">
                    <h3 className="text-lg font-medium text-[#202124]">{story.business_name || story.name}</h3>
                    {story.is_verified && <BadgeCheck className="w-5 h-5 text-[#1a73e8]" />}
                  </div>
                  <p className="text-sm text-[#1a73e8] mb-3">{story.service_type || story.specialty}</p>
                  <p className="text-[#5f6368] text-sm mb-4">{story.story || story.testimonial}</p>
                  <div className="flex items-center justify-between pt-4 border-t border-[#f1f3f4]">
                    <div className="flex items-center gap-1">
                      <TrendingUp className="w-4 h-4 text-[#1e8e3e]" />
                      <span className="text-sm font-medium text-[#1e8e3e]">{story.growth || "+150%"} growth</span>
                    </div>
                    <div className="flex items-center gap-1">
                      <Star className="w-4 h-4 text-[#f9ab00] fill-[#f9ab00]" />
                      <span className="text-sm text-[#5f6368]">{story.rating?.toFixed(1) || "4.9"}</span>
                    </div>
                  </div>
                </div>
              </div>
            )) : (
              // Fallback success stories
              [
                {
                  name: "Mike's Plumbing",
                  specialty: "Plumbing Services",
                  story: "Since joining LocalPros, my business has grown from 2 to 8 employees. The quality leads and easy-to-use platform have been game changers for my small business.",
                  growth: "+200%",
                  rating: "4.9",
                  jobs: "150+"
                },
                {
                  name: "Spark Electric Co.",
                  specialty: "Electrical Services",
                  story: "LocalPros helped me transition from working for someone else to running my own successful electrical business. The verified badge really helps build trust with new customers.",
                  growth: "+175%",
                  rating: "4.8",
                  jobs: "200+"
                },
                {
                  name: "CleanPro Solutions",
                  specialty: "Cleaning Services",
                  story: "What started as a side hustle is now my full-time business thanks to LocalPros. The subscription plan gives me a steady stream of quality leads every month.",
                  growth: "+250%",
                  rating: "5.0",
                  jobs: "300+"
                }
              ].map((story, idx) => (
                <div 
                  key={idx} 
                  className="bg-white rounded-2xl overflow-hidden border border-[#dadce0] hover:shadow-lg transition-all hover:-translate-y-1"
                  data-testid={`success-story-${idx}`}
                >
                  <div className="h-48 bg-gradient-to-br from-[#e8f0fe] to-[#f8f9fa] flex items-center justify-center">
                    <div className="w-24 h-24 rounded-full bg-white flex items-center justify-center shadow-lg">
                      <span className="text-3xl font-medium text-[#1a73e8]">
                        {story.name.charAt(0)}
                      </span>
                    </div>
                  </div>
                  <div className="p-6">
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="text-lg font-medium text-[#202124]">{story.name}</h3>
                      <BadgeCheck className="w-5 h-5 text-[#1a73e8]" />
                    </div>
                    <p className="text-sm text-[#1a73e8] mb-3">{story.specialty}</p>
                    <p className="text-[#5f6368] text-sm mb-4">{story.story}</p>
                    <div className="flex items-center justify-between pt-4 border-t border-[#f1f3f4]">
                      <div className="flex items-center gap-1">
                        <TrendingUp className="w-4 h-4 text-[#1e8e3e]" />
                        <span className="text-sm font-medium text-[#1e8e3e]">{story.growth} growth</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="w-4 h-4 text-[#f9ab00] fill-[#f9ab00]" />
                        <span className="text-sm text-[#5f6368]">{story.rating}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
          
          <div className="text-center mt-10">
            <Button
              variant="outline"
              onClick={() => navigate("/join")}
              className="rounded-full border-[#1a73e8] text-[#1a73e8] hover:bg-[#e8f0fe] px-8 py-3 h-auto"
              data-testid="become-success-story-btn"
            >
              {t("successStories.becomeSuccess")} <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Featured Jobs Section */}
      {featuredJobs.length > 0 && (
        <section className="py-16" data-testid="featured-jobs-section">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium text-[#202124]">
                  {t("common.featured")} {t("leads.title")}
                </h2>
                <p className="text-[#5f6368] mt-2">{t("leads.subtitle")}</p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/jobs")}
                className="rounded-full border-[#dadce0] hidden md:flex"
              >
                {t("common.viewAll")} <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredJobs.map((job) => {
                const Icon = iconMap[job.service_category];
                return (
                  <Link
                    key={job.id}
                    to="/jobs"
                    className="bg-white rounded-2xl border border-[#dadce0] p-6 hover:shadow-lg transition-shadow"
                    data-testid={`featured-job-${job.id}`}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium flex items-center gap-1 ${serviceColors[job.service_category] || 'bg-gray-100 text-gray-700'}`}>
                        {Icon && <Icon className="w-4 h-4" />}
                        {job.service_category?.charAt(0).toUpperCase() + job.service_category?.slice(1)}
                      </span>
                      <span className="text-xs text-[#5f6368]">
                        {new Date(job.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-medium text-[#202124] mb-2">{job.title}</h3>
                    <p className="text-sm text-[#5f6368] mb-4 line-clamp-2">{job.description}</p>
                    
                    <div className="flex items-center justify-between pt-4 border-t border-[#dadce0]">
                      <div className="flex items-center gap-2 text-sm text-[#5f6368]">
                        <MapPin className="w-4 h-4" />
                        {job.location}
                      </div>
                      {(job.budget_min || job.budget_max) && (
                        <div className="flex items-center gap-1 text-sm text-[#1e8e3e]">
                          <DollarSign className="w-4 h-4" />
                          {job.budget_min || 0} - {job.budget_max || "Flex"}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
            
            <div className="text-center mt-8 md:hidden">
              <Button
                onClick={() => navigate("/jobs")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                View All Jobs <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Top Rated Professionals */}
      {topWorkers.length > 0 && (
        <section className="py-16 bg-[#f8f9fa]" data-testid="top-workers-section">
          <div className="container-custom">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="text-3xl md:text-4xl font-medium text-[#202124]">
                  Top Rated Professionals
                </h2>
                <p className="text-[#5f6368] mt-2">Trusted contractors with excellent reviews</p>
              </div>
              <Button
                variant="outline"
                onClick={() => navigate("/browse")}
                className="rounded-full border-[#dadce0] hidden md:flex"
              >
                Browse All <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {topWorkers.map((worker) => (
                <Link
                  key={worker.id}
                  to={`/profile/${worker.id}`}
                  className="bg-white rounded-2xl border border-[#dadce0] p-6 hover:shadow-lg transition-shadow"
                  data-testid={`top-worker-${worker.id}`}
                >
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 rounded-xl bg-[#e8f0fe] flex items-center justify-center overflow-hidden">
                      {worker.profile_photo ? (
                        <img src={worker.profile_photo} alt={worker.name} className="w-full h-full object-cover" />
                      ) : (
                        <span className="text-xl font-medium text-[#1a73e8]">
                          {worker.name?.charAt(0).toUpperCase()}
                        </span>
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-[#202124]">
                          {worker.business_name || worker.name}
                        </h3>
                        {worker.is_verified && (
                          <BadgeCheck className="w-4 h-4 text-[#1a73e8]" />
                        )}
                      </div>
                      <div className="flex items-center gap-1 mt-1">
                        <Star className="w-4 h-4 text-[#f9ab00] fill-[#f9ab00]" />
                        <span className="text-sm font-medium text-[#202124]">{worker.rating?.toFixed(1)}</span>
                        <span className="text-sm text-[#5f6368]">({worker.reviews_count} reviews)</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {worker.services?.slice(0, 3).map((service) => (
                      <span key={service} className="text-xs px-2 py-1 bg-[#f8f9fa] text-[#5f6368] rounded-full">
                        {service?.charAt(0).toUpperCase() + service?.slice(1)}
                      </span>
                    ))}
                  </div>
                  
                  <div className="flex items-center gap-2 mt-4 pt-4 border-t border-[#dadce0] text-sm text-[#5f6368]">
                    <MapPin className="w-4 h-4" />
                    {worker.location}
                  </div>
                </Link>
              ))}
            </div>
            
            <div className="text-center mt-8 md:hidden">
              <Button
                onClick={() => navigate("/browse")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] rounded-full"
              >
                Browse All Pros <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Join as Pro CTA */}
      <section className="py-20 bg-white" data-testid="join-cta-section">
        <div className="container-custom">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="order-2 md:order-1">
              <img
                src="https://images.pexels.com/photos/8486978/pexels-photo-8486978.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt="Professional worker"
                className="rounded-3xl shadow-xl w-full"
              />
            </div>
            <div className="order-1 md:order-2">
              <div className="text-sm font-medium text-[#1a73e8] mb-4 uppercase tracking-wider">For Professionals</div>
              <h2 className="text-3xl md:text-4xl font-medium text-[#202124] mb-6">
                Grow your business with quality leads
              </h2>
              <p className="text-[#5f6368] mb-8 text-lg">
                Join our network of trusted professionals. Get connected with homeowners 
                in your area who need your services.
              </p>
              <ul className="space-y-3 mb-8">
                {["Free to join", "No subscription fees", "Pay only for leads you accept", "Build your reputation"].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-3 text-[#202124]">
                    <CheckCircle className="w-5 h-5 text-[#1e8e3e]" />
                    {item}
                  </li>
                ))}
              </ul>
              <Button
                onClick={() => navigate("/join")}
                className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto text-base font-medium"
                data-testid="join-as-pro-btn"
              >
                Join as a Pro <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="section-spacing text-center" data-testid="final-cta-section">
        <div className="container-custom">
          <h2 className="text-3xl md:text-4xl font-medium text-[#202124] mb-4">
            Ready to get started?
          </h2>
          <p className="text-[#5f6368] mb-8 max-w-xl mx-auto">
            Tell us about your project and we'll connect you with the right pros instantly.
          </p>
          <Button
            onClick={() => navigate("/request")}
            className="bg-[#1a73e8] hover:bg-[#1557b0] text-white rounded-full px-8 py-3 h-auto text-base font-medium"
            data-testid="request-service-cta-btn"
          >
            Request a Service <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HomePage;
